import { jwtDecode } from 'jwt-decode';
import React, { useEffect, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import './Auth.css';
import { FaRegUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { authAPI } from '../../Api/api';
import GoogleButton from "./GoogleButton/GoogleButton";
import AuthContext from '../../Context/AuthContext';
import {useTranslation} from "react-i18next";
import Button from '../../Component/UI/Button/Button';
import { getGoogleAuthRedirectError } from '../../Utils/googleAuthError';
import LanguageContext from '../../Context/LanguageContext';

function Login({ navigate }) {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const lang = i18n.language;

    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState('');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [authenticate, setAuthenticate] = useState(false);

    const { refreshUser } = useContext(AuthContext);
    const location = useLocation();

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    // Check for error in URL query params (e.g., from Google auth failure)
    useEffect(() => {
        const urlError = getGoogleAuthRedirectError(location.search, t);
        if (urlError) {
            setError(urlError);
        }
    }, [location.search, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const response = await authAPI.login({
                email: formData.email,
                password: formData.password
            });
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('userId', response.data.user._id);

            setAuthenticate(true);
            refreshUser(); // Refresh user data
            navigate(`/${lang}/profile`);
        } catch (err) {
            console.error('Error response:', err.response);
            const errorMsg = err.response?.data?.message || t('loginPage.unexpectedError');
            setError(errorMsg);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setError('');
    };

    const handleGoogleAuth = () => {
        try {
            window.location.href = authAPI.googleAuth({ intent: 'login' });
        } catch (err) {
            setError(err.message || t('loginPage.unexpectedError'));
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                const currentTime = Date.now() / 1000;
                if (decodedToken.exp > currentTime) {
                    setAuthenticate(true);
                    navigate(`/${lang}/profile`);
                } else {
                    localStorage.removeItem('token');
                    localStorage.removeItem('userId');
                    setAuthenticate(false);
                }
            } catch (error) {
                localStorage.removeItem('token');
                localStorage.removeItem('userId');
                setAuthenticate(false);
            }
        }
    }, [lang, navigate]);

    const reverseClass = dir() === 'rtl' ? 'reverse' : '';


    return (
        <main className='auth-main' lang={['fa', 'ps', 'ar'].includes(lang) ? lang : 'en'}>
            {!authenticate && (
                <section className='section auth-public-section'>
                    <div className='login-container container '>

                        <form className="loginForm" onSubmit={handleSubmit}>
                            <h1 className="account-form-title">
                                {t('loginPage.signIn')}
                            </h1>

                            <GoogleButton onClick={handleGoogleAuth} />

                            <div className="loginContent">
                                <div className={`loginBox ${reverseClass}`}>
                                    <FaRegUser/>
                                    <div className="loginBoxInput">
                                        <input
                                            type="email"
                                            required
                                            className="loginInput"
                                            placeholder=" "
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                        />
                                        <label className="loginLabel">
                                            {t('loginPage.email')}
                                        </label>
                                    </div>
                                </div>

                                <div className={`loginBox ${reverseClass}`}>
                                    <FaLock className='loginIcon'/>
                                    <div className="loginBoxInput">
                                        <input
                                            type={passwordVisible ? 'text' : 'password'}
                                            className="loginInput"
                                            placeholder=" "
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            required
                                        />
                                        <label className="loginLabel">
                                            {t('loginPage.password')}
                                        </label>
                                        <button
                                            type="button"
                                            onClick={togglePasswordVisibility}
                                            aria-label={passwordVisible ? t('loginPage.hidePassword') : t('loginPage.showPassword')}
                                        >
                                            {passwordVisible ? <FaEyeSlash className='a-eye-icon'/> : <FaEye className='a-eye-icon'/>}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="loginCheck">
                                <div className="loginCheckGroup">
                                    <input type="checkbox" className="loginCheckInput"/>
                                    <label className="loginCheckLabel">
                                        {t('loginPage.rememberMe')}
                                    </label>
                                </div>
                                <span
                                    className="loginForgot"
                                    onClick={() => navigate(`/${lang}/forgot-password`)}
                                >
                                    {t('loginPage.forgotPassword')}
                                </span>
                            </div>

                            <Button className="loginButton" type='submit'>
                                {t('loginPage.signIn')}
                            </Button>

                            <p className="loginRegister">
                                {t('loginPage.dontHaveAccount')}
                                <strong
                                    className='auth-link'
                                    onClick={() => navigate(`/${lang}/sign-up`)}
                                >
                                    {' '}{t('loginPage.signUp')}
                                </strong>
                            </p>
                            {error && <p className="auth-form-error" role="alert">{error}</p>}
                        </form>
                    </div>
                </section>
            )}
        </main>
    );
}

export default Login;